import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportChecklistUnits,
    IChecklist2ReportCheckpoints,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { BehaviorSubject } from 'rxjs';
import { Animations } from '@fi-shared/utils/animations';
import { FiLocalSearch2Component } from '@fi-shared/fi-local-search2/fi-local-search2.component';
import { isDefined, isEqual } from '@fi-shared/utils';
import { sortArrayByProperty } from '@fi-shared/utils/array-sort-comparators';
import { MatSortDirection } from '@fi-shared/types';
import { FiChecklist2ReportHelperService } from '../../../../services/fi-checklist2-report-helper.service';
import { FiChecklistDetailsUnitsTableComponent } from '@fi-shared/checklist/fi-checklist2-report/components/open-checklists-report/open-checklist-details/checklist-details-tab-group/checklist-details-units-table/checklist-details-units-table.component';
import { FiChecklistDetailsCheckpointsTableComponent } from '@fi-shared/checklist/fi-checklist2-report/components/open-checklists-report/open-checklist-details/checklist-details-tab-group/checklist-details-checkpoints-table/checklist-details-checkpoints-table.component';

export enum ReportChecklistProgressTabIndex {
    BusinessUnit,
    Checkpoints,
}

@Component({
    selector: 'fi-checklist-details-tab-group',
    templateUrl: './checklist-details-tab-group.component.html',
    styleUrls: ['./checklist-details-tab-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-details-tab-group' },
    animations: [Animations.slideInOut()],
    standalone: false,
})
export class FiChecklistDetailsTabGroupComponent implements OnInit, OnChanges {
    @Input() checklistUnits: IChecklist2ReportChecklistUnits[] = [];
    @Input() checkpoints: IChecklist2ReportCheckpoints[] = [];
    @Input() checklistSummary: IChecklist2ReportChecklistSummary;
    @Input() currentFilter: IChecklistV2ReportParameterFilter;
    @Input() isAuditMode: boolean;
    @Input() isUnitsInitialLoadFinished$ = new BehaviorSubject<boolean>(false);

    @ViewChild('fiLocalSearch2Component') fiLocalSearch2Component: FiLocalSearch2Component;
    reportChecklistProgressTabIndex = ReportChecklistProgressTabIndex;
    selectedTabIndex: number = ReportChecklistProgressTabIndex.BusinessUnit;
    searchText$ = new BehaviorSubject<string>('');
    filteredCheckpoints$ = new BehaviorSubject<IChecklist2ReportCheckpoints[]>(this.checkpoints);
    filteredChecklistUnits$ = new BehaviorSubject<IChecklist2ReportChecklistUnits[]>(this.checklistUnits);
    isSearchTextHighlighted = new BehaviorSubject<boolean>(false);
    private readonly unitDeviationCountColumnName: string = 'DeviationsCount';
    private readonly unitFilledCheckpointsCountColumnName: string = 'FilledCheckpointsCount';

    @ViewChild('checklistUnitsTableComponent') checklistUnitsTableComponent: FiChecklistDetailsUnitsTableComponent;
    @ViewChild('checkpointsTableComponent') checkpointsTableComponent: FiChecklistDetailsCheckpointsTableComponent;

    constructor(
        private cdr: ChangeDetectorRef,
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
    ) {}

    ngOnInit() {
        this.filteredCheckpoints$.next(this.checkpoints);
        this.filteredChecklistUnits$.next(this.checklistUnits);
    }

    ngOnChanges(changes: SimpleChanges) {
        let change = changes.checkpoints;
        if (!!change && !isEqual(change.currentValue, change.previousValue)) {
            this.onCheckpointsSearchChange(this.searchText$.value);
            this.cdr.markForCheck();
        }

        change = changes.checklistUnits;
        if (!!change && !isEqual(change.currentValue, change.previousValue)) {
            this.filteredChecklistUnits$.next(change.currentValue);
            this.sortChecklistUnits();
            this.cdr.markForCheck();
        }
    }

    setSelectedTabIndex(tabIndex: ReportChecklistProgressTabIndex) {
        this.selectedTabIndex = tabIndex;
        if (this.isBusinessUnitTabSelected() && !!this.searchText$.value) {
            this.fiLocalSearch2Component.resetSimpleSearchText();
        }
    }

    isBusinessUnitTabSelected(): boolean {
        return this.selectedTabIndex === ReportChecklistProgressTabIndex.BusinessUnit;
    }

    onCheckpointsSearchChange(newSearchText: string) {
        this.filteredCheckpoints$.next(
            newSearchText === ''
                ? this.checkpoints
                : this.checkpoints.filter((checkpoint) => {
                      return checkpoint.CheckpointName.toLowerCase().includes(newSearchText.toLowerCase());
                  }),
        );
        this.searchText$.next(newSearchText);
    }

    setSearchTextHighlight(isHighlighted: boolean) {
        this.isSearchTextHighlighted.next(isHighlighted);
    }

    sortChecklistUnits() {
        if (!isDefined(this.checklistUnitsTableComponent)) return;

        const isDescendingSort: boolean =
            this.checklistUnitsTableComponent.selectedSort.direction === MatSortDirection.Descending;
        const sortColumn: string = this.checklistUnitsTableComponent.selectedSort.active;
        if ([this.unitDeviationCountColumnName, this.unitFilledCheckpointsCountColumnName].includes(sortColumn)) {
            const sortingResult: IChecklist2ReportChecklistUnits[] = sortArrayByProperty(
                this.filteredChecklistUnits$.value,
                this.getSortFuncByColumnName(sortColumn),
                isDescendingSort,
            );

            this.filteredChecklistUnits$.next([...sortingResult]);
        }
    }

    getSortFuncByColumnName(columnName: string): (item: IChecklist2ReportChecklistUnits) => number {
        return this.unitDeviationCountColumnName === columnName
            ? this.getDeviationsCount.bind(this)
            : this.getFilledCheckpointsCount.bind(this);
    }

    private getDeviationsCount(checklistDetails: IChecklist2ReportChecklistUnits): number {
        return this.fiChecklist2ReportHelperService.getNotOkCheckpoints(checklistDetails.AnswerDetails)?.length;
    }

    private getFilledCheckpointsCount(checklistUnits: IChecklist2ReportChecklistUnits): number {
        return this.fiChecklist2ReportHelperService.getChecklistUnitsFilledCheckpoints(checklistUnits);
    }
}
